import {
    ATTRIBUTE_POINTS_PER_MILESTONE,
    findEssenceAbilityForSkill,
    findSkillCatalogEntry,
    findSkillGroup,
    findSkillPerks,
    hasSkillPerk,
    SKILL_POINTS_PER_MILESTONE,
    resolveEssenceRing,
    resolvePraxisToAdvance,
    type SkillActivation,
    type SkillBranch,
    type SkillDecayRisk,
} from '@sorrem/shared';
import {
    SKILL_TREE_BRANCH_RULES,
    SKILL_TREE_MARTIAL_TIERS,
    type SkillTreeDialModel,
    type SkillTreeGroupModel,
    type SkillTreeNodeModel,
} from './skill-tree-model';
import { countOf } from '@sorrem-client/ui/shared/text';

/**
 * The words the Skills tab's right panel shows for a selected node or dial.
 * Blurbs are the agreed text in docs/gameplay/skill-tree-ui.md (Dial Blurbs);
 * "grows by" follows docs/skills.md (Runtime Praxis Awards).
 */

export interface SkillTreeDialDetails {
    readonly title: string;
    readonly blurb: string;
    readonly status: string;
    /** The milestone or tier track, in order. */
    readonly milestones: readonly { readonly label: string; readonly percent: number; readonly isReached: boolean }[];
}

export interface SkillTreeNodeDetails {
    readonly title: string;
    /** E.g. "Level 6 of 10", or "Ring 2 of 5" for an Essence skill. */
    readonly levelText: string;
    /** An Essence skill's ability: its name and what it does, or null when it has none yet. Undefined for other skills. */
    readonly ability?: { readonly name: string; readonly description: string; readonly costText: string } | null;
    /** Praxis toward the next step, or null once mastered. */
    readonly praxis: { readonly current: number; readonly needed: number; readonly fraction: number } | null;
    readonly decayText: string;
    readonly activeText: string;
    readonly growsBy: string;
    /** Its perks, in unlock order: what each does, when it unlocks, and whether it's active now. */
    readonly perks: readonly SkillTreePerkDetails[];
}

export interface SkillTreePerkDetails {
    readonly name: string;
    readonly description: string;
    /** E.g. "Level 3", or "Ring 2" for an Essence perk. */
    readonly unlockText: string;
    readonly isActive: boolean;
}

/** What each branch calls a step on its skills' markers: Essence skills are raised ring by ring (#145). */
const STEP_WORDS: Record<SkillBranch, string> = {
    martial: 'Level',
    essence: 'Ring',
    crafting: 'Level',
};

/** When a perk unlocks, by its skill level: Essence perks by the ring that level stands on. */
const PERK_UNLOCK_TEXT: Record<SkillBranch, (level: number) => string> = {
    martial: (level) => `Level ${level}`,
    essence: (level) => `Ring ${resolveEssenceRing(level)}`,
    crafting: (level) => `Level ${level}`,
};

/** "Level 6 of 10", "Ring 5 of 5 - mastered": a skill's place on its branch's marker. */
function formatSkillStep(node: SkillTreeNodeModel): string {
    const word = STEP_WORDS[node.placement.branch];
    return node.isMastered ? `${word} ${node.maxStep} of ${node.maxStep} - mastered` : `${word} ${node.step} of ${node.maxStep}`;
}

/** A skill's perks and which are active at its current level (decay switches Martial ones off). */
function describePerks(node: SkillTreeNodeModel): SkillTreePerkDetails[] {
    const unlockText = PERK_UNLOCK_TEXT[node.placement.branch];
    return findSkillPerks(node.placement.id).map((perk) => ({
        name: perk.name,
        description: perk.description,
        unlockText: unlockText(perk.level),
        isActive: hasSkillPerk(node.skill ? [node.skill] : undefined, perk.id),
    }));
}

/** Player-facing definition of praxis, shown as a tooltip where it first appears. */
export const PRAXIS_DEFINITION = 'Praxis: practice that builds toward a skill\'s next level.';

/** What each Veil Attunement milestone grants, as the shared rules give it. */
const MILESTONE_ATTRIBUTE_POINTS = countOf(ATTRIBUTE_POINTS_PER_MILESTONE, 'attribute point');
const MILESTONE_SKILL_POINTS = countOf(SKILL_POINTS_PER_MILESTONE, 'skill point');

const DIAL_TEXT: Record<SkillBranch, { blurb: string; status: string }> = {
    martial: {
        blurb: 'The body remembers only what the hand keeps practicing. Every blade, haft, and bowstring you train sharpens your discipline; set them aside, and the edge dulls.',
        status: 'Rises and falls with your weapon skills.',
    },
    essence: {
        blurb: 'The Veil touched you by chance, but how deeply it reaches is yours to choose. Each time you shape essence, it opens a little further, and what it opens it never closes again.',
        status: `Grows as your abilities spend essence. Never fades. Each milestone grants ${MILESTONE_ATTRIBUTE_POINTS} and ${MILESTONE_SKILL_POINTS}.`,
    },
    crafting: {
        blurb: 'Essence carried out of a Crucible lies inert until a crafter draws it out and works it into steel, glass, and stone. Every piece you finish teaches the hand a little more, from Apprentice to Artisan.',
        status: 'Never fades.',
    },
};

const UNAWAKENED_CRAFTING_TEXT = {
    blurb: 'The essence you carry out of the Crucibles waits for hands that can work it. The craft has not yet woken in you.',
    status: 'Not yet awakened.',
};

/** The Martial Discipline tier name at `percent`. */
export function resolveMartialTierName(percent: number): string {
    return [...SKILL_TREE_MARTIAL_TIERS].reverse().find((tier) => percent >= tier.percent)?.label ?? 'Raw';
}

export function describeSkillTreeDial(dial: SkillTreeDialModel): SkillTreeDialDetails {
    const { branch, name } = dial.placement;
    const text = dial.isUnawakened ? UNAWAKENED_CRAFTING_TEXT : DIAL_TEXT[branch];
    // Martial and Crafting name their tiers; Veil Attunement's numbered milestones each grant points.
    const { tiers } = SKILL_TREE_BRANCH_RULES[branch];
    const milestones = tiers
        ? tiers.map((tier) => ({ label: tier.label, percent: tier.percent, isReached: dial.percent >= tier.percent }))
        : dial.studs.map((stud) => ({ label: `+${MILESTONE_ATTRIBUTE_POINTS}, +${MILESTONE_SKILL_POINTS}`, percent: stud.percent, isReached: stud.isReached }));
    return { title: name, blurb: text.blurb, status: text.status, milestones };
}

const DECAY_TEXT: Record<SkillDecayRisk, string> = {
    none: 'Never decays',
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
};

const ACTIVATION_TEXT: Record<SkillActivation, { active: string; inactive: string }> = {
    always: { active: 'Always active', inactive: 'Always active' },
    'requires-melee': { active: 'Active: melee weapon equipped', inactive: 'Inactive: needs a melee weapon' },
    'requires-range': { active: 'Active: ranged weapon equipped', inactive: 'Inactive: needs a ranged weapon' },
    'requires-dual': { active: 'Active: both weapon slots equipped', inactive: 'Inactive: needs a weapon in both slots' },
    'requires-shield': { active: 'Active: shield equipped', inactive: 'Inactive: needs a shield' },
    'requires-unarmed': { active: 'Active: no weapon in hand', inactive: 'Inactive: needs empty hands (a shield is fine)' },
};

/** An Essence skill: its ring, raised with skill points, and the ability it grants. */
function describeEssenceSkillNode(node: SkillTreeNodeModel): SkillTreeNodeDetails {
    const ability = findEssenceAbilityForSkill(node.placement.id);
    return {
        title: node.name,
        levelText: formatSkillStep(node),
        ability: ability
            ? { name: ability.name, description: ability.description, costText: `${ability.essenceCost} essence - ${ability.cooldownMs / 1000}s cooldown` }
            : null,
        praxis: null,
        decayText: DECAY_TEXT.none,
        activeText: ACTIVATION_TEXT.always.active,
        growsBy: 'Skill points, earned as Veil Attunement grows.',
        perks: describePerks(node),
    };
}

/** A Martial (or unawakened Crafting) skill: its level, praxis toward the next, decay and gear. */
function describeLeveledSkillNode(node: SkillTreeNodeModel): SkillTreeNodeDetails {
    const { skill, placement } = node;
    const activation = ACTIVATION_TEXT[skill?.activation ?? 'always'];
    const needed = skill ? resolvePraxisToAdvance(skill) : 0;
    return {
        title: node.name,
        levelText: formatSkillStep(node),
        praxis: node.isMastered || !skill || needed === 0
            ? null
            : { current: skill.praxis, needed, fraction: node.progress },
        decayText: DECAY_TEXT[node.decayRisk],
        activeText: node.isInactive ? activation.inactive : activation.active,
        growsBy: node.isUnawakened
            ? 'Crafting, once it awakens.'
            : findSkillCatalogEntry(placement.id)?.growsBy ?? (node.isTrained ? 'Using it in a Crucible.' : 'Not trained in the Crucible yet: its rules are still being designed.'),
        perks: describePerks(node),
    };
}

export function describeSkillTreeNode(node: SkillTreeNodeModel): SkillTreeNodeDetails {
    const { branch } = node.placement;
    switch (branch) {
        case 'essence':
            return describeEssenceSkillNode(node);
        case 'martial':
        case 'crafting':
            return describeLeveledSkillNode(node);
        default: {
            const unknownBranch: never = branch;
            throw new Error(`Unknown skill branch: ${String(unknownBranch)}`);
        }
    }
}

export interface SkillTreeGroupDetails {
    readonly title: string;
    readonly blurb: string;
    /** Its skills, in tree order, with each one's level (or tier) text. */
    readonly skills: readonly { readonly id: string; readonly name: string; readonly levelText: string }[];
}

export function describeSkillTreeGroup(group: SkillTreeGroupModel): SkillTreeGroupDetails {
    return {
        title: group.placement.name,
        blurb: findSkillGroup(group.placement.id)?.blurb ?? '',
        skills: group.nodes.map((node) => ({
            id: node.placement.id,
            name: node.name,
            levelText: node.isUnawakened
                ? 'Not yet awakened'
                : node.isMastered
                    ? 'Mastered'
                    : `${STEP_WORDS[node.placement.branch]} ${node.step} of ${node.maxStep}`,
        })),
    };
}
